import React, { useEffect, useState, useCallback } from 'react';
import { Routes, Route, NavLink, Navigate, useNavigate, useSearchParams, useLocation, useParams } from 'react-router-dom';
import { LogIn, Eye, EyeOff, LogOut, Home, Receipt, FolderKanban, Upload, CheckCircle2, Lock, FolderOpen, Globe, Bell, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';
import ThemeToggle from '../../components/ThemeToggle';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';

const api = axios.create({ baseURL: '/api/portal' });
api.interceptors.request.use(c => {
  const t = localStorage.getItem('portal_token');
  if (t) c.headers.Authorization = 'Bearer ' + t;
  return c;
});

/* Tema del portal: no hay sesión del CRM acá, así que se guarda en el navegador */
const leerTema = () => { try { return localStorage.getItem('crm_theme') === 'dark' ? 'dark' : 'light'; } catch { return 'light'; } };
const aplicarTema = (t) => {
  document.documentElement.dataset.theme = t;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', t === 'light' ? '#ffffff' : '#050b1a');
  try { localStorage.setItem('crm_theme', t); } catch { /* modo privado */ }
};
// un link pegado sin http lo tomaría como ruta interna
const externo = (u) => { const v = String(u || '').trim(); return !v ? null : (/^https?:\/\//i.test(v) ? v : 'https://' + v.replace(/^\/+/, '')); };
const money = (v) => '$' + Number(v || 0).toLocaleString('es-AR');
const fecha = (d) => (d ? String(d).slice(0, 10).split('-').reverse().join('/') : '—');
const ESTADOS = {
  pendiente: { label: 'Pendiente', color: '#f59e0b' }, vencido: { label: 'Vencido', color: '#ef4444' },
  informado: { label: 'Pago informado', color: '#0ea5e9' }, pagado: { label: 'Pagado', color: '#22c55e' },
};

/* Marco de las pantallas de acceso: degradé de marca + tarjeta flotante */
const DEGRADE = 'linear-gradient(135deg, #0B1C33 0%, #14487F 55%, #2E7DE2 100%)';
function PantallaAcceso({ titulo, bajada, children }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, background: DEGRADE }}>
      <div style={{ width: '100%', maxWidth: 410 }}>
        <div style={{ textAlign: 'center', marginBottom: 22 }}>
          <img src="/icon-192.png?v=2" alt="U.dream" style={{ width: 64, height: 64, borderRadius: 18, boxShadow: '0 10px 30px rgba(0,0,0,0.35)' }} />
          <div style={{ marginTop: 12, fontSize: 11, letterSpacing: 2.5, textTransform: 'uppercase', color: 'rgba(255,255,255,0.72)', fontWeight: 600 }}>Udream App</div>
        </div>
        <div style={{ padding: '32px 30px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 18, boxShadow: '0 20px 50px rgba(11,28,51,0.35)' }}>
          <h2 style={{ fontSize: 19, fontWeight: 800, marginBottom: bajada ? 6 : 18 }}>{titulo}</h2>
          {bajada && <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 18, lineHeight: 1.5 }}>{bajada}</p>}
          {children}
        </div>
        <div style={{ textAlign: 'center', marginTop: 18, fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>udream.com.ar</div>
      </div>
    </div>
  );
}

/* ---------------- Login ---------------- */
function Login({ onEntrar }) {
  const [form, setForm] = useState({ email: '', password: '' });
  const [ver, setVer] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [olvide, setOlvide] = useState(false);

  const entrar = async (e) => {
    e.preventDefault();
    setCargando(true);
    try {
      const r = await api.post('/login', form);
      localStorage.setItem('portal_token', r.data.token);
      onEntrar(r.data.usuario);
    } catch (err) { toast.error(err.response?.data?.error || 'No pudimos entrar'); }
    finally { setCargando(false); }
  };
  const recuperar = async (e) => {
    e.preventDefault();
    await api.post('/olvide', { email: form.email }).catch(() => {});
    toast.success('Si el email existe, te enviamos las instrucciones', { duration: 6000 });
    setOlvide(false);
  };

  return (
    <PantallaAcceso titulo={olvide ? 'Recuperar el acceso' : 'Entrá a tu portal'}
      bajada={olvide ? 'Poné tu email y te mandamos un enlace para crear una contraseña nueva.' : 'Tus servicios, facturas y el avance de tus proyectos, en un solo lugar.'}>
      {olvide ? (
        <form onSubmit={recuperar}>
          <div className="form-group"><label>Email</label><input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required autoFocus /></div>
          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: 12 }} type="submit">Enviar instrucciones</button>
          <button type="button" className="btn btn-ghost btn-sm" style={{ width: '100%', justifyContent: 'center', marginTop: 10 }} onClick={() => setOlvide(false)}>Volver</button>
        </form>
      ) : (
        <form onSubmit={entrar}>
          <div className="form-group"><label>Email</label><input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required autoFocus /></div>
          <div className="form-group"><label>Contraseña</label>
            <div style={{ position: 'relative' }}>
              <input type={ver ? 'text' : 'password'} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required style={{ paddingRight: 42 }} />
              <button type="button" onClick={() => setVer(!ver)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                {ver ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: 12 }} disabled={cargando} type="submit">
            <LogIn size={16} /> {cargando ? 'Entrando...' : 'Entrar'}
          </button>
          <button type="button" className="btn btn-ghost btn-sm" style={{ width: '100%', justifyContent: 'center', marginTop: 10 }} onClick={() => setOlvide(true)}>Olvidé mi contraseña</button>
        </form>
      )}
    </PantallaAcceso>
  );
}

/* ---------------- Recuperar contraseña ---------------- */
function Recuperar() {
  const [params] = useSearchParams();
  const [pass, setPass] = useState({ a: '', b: '' });
  const [listo, setListo] = useState(false);
  const navigate = useNavigate();
  const guardar = async (e) => {
    e.preventDefault();
    if (pass.a !== pass.b) return toast.error('Las contraseñas no coinciden');
    try { await api.post('/recuperar', { token: params.get('token'), password: pass.a }); setListo(true); }
    catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };
  return (
    <PantallaAcceso titulo={listo ? 'Contraseña actualizada' : 'Creá tu contraseña'}
      bajada={listo ? 'Ya podés entrar al portal con la contraseña nueva.' : 'Elegí una contraseña de al menos 8 caracteres.'}>
      {listo ? (
        <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: 12 }} onClick={() => navigate('/', { replace: true })}>
          <LogIn size={16} /> Ir al portal
        </button>
      ) : (
        <form onSubmit={guardar}>
          <div className="form-group"><label>Contraseña</label><input type="password" value={pass.a} onChange={e => setPass({ ...pass, a: e.target.value })} required minLength={8} autoFocus /></div>
          <div className="form-group"><label>Repetir contraseña</label><input type="password" value={pass.b} onChange={e => setPass({ ...pass, b: e.target.value })} required /></div>
          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: 12 }} type="submit">Guardar</button>
        </form>
      )}
    </PantallaAcceso>
  );
}

/* ---------------- Inicio ---------------- */
const Acceso = ({ icono: Icono, titulo, valor, detalle, onClick, color }) => (
  <div className="card" style={{ padding: '14px 16px', cursor: 'pointer', display: 'flex', gap: 12, alignItems: 'center' }} onClick={onClick}>
    <div style={{ width: 38, height: 38, borderRadius: 11, background: 'var(--accent-glow)', color: color || 'var(--accent)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icono size={19} />
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>{titulo}</div>
      <div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1.2, color: color || 'var(--text-primary)' }}>{valor}</div>
      {detalle && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{detalle}</div>}
    </div>
  </div>
);

function Inicio({ onPagar }) {
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  const cargar = useCallback(() => { api.get('/inicio').then(r => setD(r.data)).catch(() => {}); }, []);
  useEffect(() => { cargar(); }, [cargar]);
  if (!d) return <div style={{ color: 'var(--text-muted)' }}>Cargando...</div>;

  const card = { marginBottom: 18 };
  const td = { padding: '10px 8px', borderTop: '1px solid var(--border)', fontSize: 13 };
  const cobrables = (d.cobros || []).filter(c => c.habilitado && c.estado !== 'informado');
  const aPagar = cobrables.reduce((a, c) => a + Number(c.monto || 0), 0);
  const servicios = (d.contratos || []).length;
  const proyectos = (d.proyectos || []).length;
  const ultimaMinuta = (d.minutas || [])[0];

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>Hola{d.usuario.nombre ? ', ' + d.usuario.nombre.split(' ')[0] : ''}</h1>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20 }}>{d.usuario.cliente}</p>

      {/* Accesos rápidos */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 22 }}>
        {d.usuario.ve_importes && (
          <Acceso icono={Receipt} titulo="Para pagar" valor={aPagar ? money(aPagar) : 'Al día'} color={aPagar ? '#f59e0b' : '#22c55e'}
            detalle={cobrables.length ? `${cobrables.length} pago${cobrables.length === 1 ? '' : 's'} disponible${cobrables.length === 1 ? '' : 's'}` : 'No tenés pagos pendientes'}
            onClick={() => navigate('/facturas')} />
        )}
        <Acceso icono={FolderKanban} titulo="Mis servicios" valor={servicios + proyectos}
          detalle={`${proyectos} proyecto${proyectos === 1 ? '' : 's'} · ${servicios} servicio${servicios === 1 ? '' : 's'}`}
          onClick={() => navigate('/proyectos')} />
        <Acceso icono={FileText} titulo="Minutas" valor={(d.minutas || []).length || '—'}
          detalle={ultimaMinuta ? `Última: ${fecha(ultimaMinuta.fecha)}` : 'Todavía no hay reuniones cargadas'}
          onClick={() => navigate('/minutas')} />
        <Acceso icono={Bell} titulo="Avisos" valor={d.sin_leer || 0} color={d.sin_leer ? 'var(--accent)' : undefined}
          detalle={d.sin_leer ? 'sin leer' : 'Estás al día'} onClick={() => navigate('/notificaciones')} />
      </div>

      {/* Pagos */}
      {d.usuario.ve_importes && d.cobros.length > 0 && (
        <div className="card" style={card}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Pagos</h3>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
            Los que están en gris todavía no están habilitados: te vamos a avisar cuando se puedan abonar.
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>{d.cobros.map(c => {
              const e = ESTADOS[c.estado] || {};
              const futuro = !c.habilitado;
              return (
                <tr key={c.id} style={{ opacity: futuro ? 0.55 : 1 }}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{c.concepto}{c.cuota_total ? ` (${c.cuota_numero}/${c.cuota_total})` : ''}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      {futuro ? 'Todavía no disponible · lo vas a poder abonar más adelante'
                        : <>Vence {fecha(c.vencimiento)} · <span style={{ color: e.color }}>{e.label}</span></>}
                    </div>
                  </td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{money(c.monto)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    {futuro
                      ? <span style={{ fontSize: 12, color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Lock size={13} /> Pendiente de habilitar</span>
                      : c.estado !== 'informado' && <button className="btn btn-primary btn-sm" onClick={() => onPagar(c)}>Ya pagué</button>}
                  </td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 18 }}>
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700 }}>Últimas novedades</h3>
            <span style={{ fontSize: 12, color: 'var(--accent)', cursor: 'pointer' }} onClick={() => navigate('/proyectos')}>Ver todo</span>
          </div>
          {(d.avances || []).length ? d.avances.map(a => (
            <div key={a.id} style={{ padding: '10px 0', borderTop: '1px solid var(--border)', fontSize: 13, cursor: 'pointer' }}
              onClick={() => navigate(a.entidad === 'proyecto' ? `/proyectos/${a.entidad_id}` : `/servicios/${a.entidad_id}`)}>
              <div style={{ fontWeight: 600 }}>{a.titulo}</div>
              {a.detalle && <div style={{ color: 'var(--text-secondary)', marginTop: 2 }}>{a.detalle}</div>}
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{a.origen} · {fecha(a.fecha)}</div>
            </div>
          )) : <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Cuando avancemos con tus trabajos, lo vas a ver acá.</p>}
        </div>

        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700 }}>Minutas de reuniones</h3>
            {(d.minutas || []).length > 0 && <span style={{ fontSize: 12, color: 'var(--accent)', cursor: 'pointer' }} onClick={() => navigate('/minutas')}>Ver todas</span>}
          </div>
          {(d.minutas || []).length ? d.minutas.slice(0, 3).map(m => (
            <div key={m.id} style={{ padding: '10px 0', borderTop: '1px solid var(--border)', fontSize: 13, cursor: 'pointer' }} onClick={() => navigate('/minutas')}>
              <div style={{ fontWeight: 600 }}>{m.titulo}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{fecha(m.fecha)}</div>
            </div>
          )) : <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Acá vas a ver el resumen de nuestras reuniones.</p>}
        </div>

        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Tus servicios</h3>
          {d.contratos.length ? d.contratos.map(c => (
            <div key={c.id} style={{ padding: '10px 0', borderTop: '1px solid var(--border)', fontSize: 13, display: 'flex', justifyContent: 'space-between', gap: 10, cursor: 'pointer' }}
              onClick={() => navigate('/servicios/' + c.id)}>
              <div><div style={{ fontWeight: 600 }}>{c.nombre}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {c.tipo === 'abono' ? 'Abono mensual' : c.tipo === 'anual' ? `Renovación anual · vence ${fecha(c.proximo_vencimiento)}` : 'Proyecto'}
                  {d.usuario.es_agencia && c.cliente !== d.usuario.cliente ? ` · ${c.cliente}` : ''}
                </div></div>
              {d.usuario.ve_importes && c.monto && <div style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{money(c.monto)}</div>}
            </div>
          )) : <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Todavía no tenés servicios cargados.</p>}
        </div>
      </div>
    </div>
  );
}

/* Todas las minutas */
function Minutas() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get('/minutas').then(r => setRows(r.data)).catch(() => setRows([])); }, []);
  if (!rows) return <div style={{ color: 'var(--text-muted)' }}>Cargando...</div>;
  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>Minutas de reuniones</h1>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20 }}>El resumen de lo que hablamos en cada encuentro.</p>
      {rows.length ? rows.map(m => (
        <details key={m.id} className="card" style={{ marginBottom: 12 }}>
          <summary style={{ cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>
            {m.titulo}<span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 12 }}> · {fecha(m.fecha)}</span>
          </summary>
          {m.detalle && <div style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-line', marginTop: 12, fontSize: 14, lineHeight: 1.7 }}>{m.detalle}</div>}
        </details>
      )) : <p style={{ color: 'var(--text-muted)' }}>Todavía no hay minutas cargadas.</p>}
    </div>
  );
}

/* ---------------- Facturas ---------------- */
function Facturas({ onPagar }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get('/facturas').then(r => setRows(r.data)).catch(() => {}); }, []);
  const td = { padding: '10px 8px', borderTop: '1px solid var(--border)', fontSize: 13 };
  const th = { textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, padding: '8px' };
  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 18 }}>Facturas y pagos</h1>
      <div className="card" style={{ padding: 0 }}>
        <div className="table-container">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>Concepto</th><th style={th}>Vence</th><th style={{ ...th, textAlign: 'right' }}>Monto</th><th style={th}>Estado</th><th style={th}>Factura</th><th style={th}></th></tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={6} style={{ ...td, color: 'var(--text-muted)' }}>Todavía no hay movimientos.</td></tr>
                : rows.map(c => {
                  const e = ESTADOS[c.estado] || {};
                  return (
                    <tr key={c.id}>
                      <td style={td}><div style={{ fontWeight: 600 }}>{c.concepto}</div>{c.cliente && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.cliente}</div>}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{fecha(c.vencimiento)}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{money(c.monto)}</td>
                      <td style={{ ...td, color: e.color, fontWeight: 600 }}>{e.label}</td>
                      <td style={td}>{c.archivo_url ? <a href={c.archivo_url} target="_blank" rel="noopener noreferrer">Descargar</a> : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                      <td style={td}>{['pendiente', 'vencido'].includes(c.estado) && <button className="btn btn-primary btn-sm" onClick={() => onPagar(c)}>Ya pagué</button>}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Proyectos ---------------- */
const ESTADO_PROY = {
  planificacion: { label: 'En planificación', color: '#f59e0b' }, en_desarrollo: { label: 'En desarrollo', color: '#0ea5e9' },
  revision: { label: 'En revisión', color: '#a855f7' }, publicacion: { label: 'En publicación', color: '#14b8a6' },
  terminado: { label: 'Terminado', color: '#22c55e' }, cancelado: { label: 'Cancelado', color: '#ef4444' },
};
const TIPO_MOV = { minuta: 'Minuta', mantenimiento: 'Mantenimiento', reporte: 'Reporte', ajuste: 'Ajuste', entrega: 'Entrega', avance: '', nota: '' };

/* Proyectos y servicios en una sola pantalla: arriba los trabajos, abajo lo que está contratado */
function Trabajos() {
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get('/trabajos').then(r => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div style={{ color: 'var(--text-muted)' }}>Cargando...</div>;

  const fila = { display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' };
  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>Mis servicios</h1>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20 }}>Entrá a cada uno para ver en qué anda, su historial y sus archivos.</p>

      {d.proyectos.length > 0 && (<>
        <h3 style={{ fontSize: 13, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 10 }}>Desarrollos y proyectos</h3>
        {d.proyectos.map(p => {
          const e = ESTADO_PROY[p.estado] || {};
          return (
            <div key={p.id} className="card" style={{ marginBottom: 12, cursor: 'pointer' }} onClick={() => navigate('/proyectos/' + p.id)}>
              <div style={fila}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{p.nombre}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                    {p.fecha_inicio ? `Desde ${fecha(p.fecha_inicio)}` : ''}{p.movimientos ? ` · ${p.movimientos} novedades` : ''}
                  </div>
                </div>
                <span className="badge" style={{ background: 'transparent', border: '1px solid var(--border)', color: e.color }}>{e.label || p.estado}</span>
              </div>
            </div>
          );
        })}
      </>)}

      {d.servicios.length > 0 && (<>
        <h3 style={{ fontSize: 13, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--text-muted)', margin: '22px 0 10px' }}>Servicios contratados</h3>
        {d.servicios.map(s => (
          <div key={s.id} className="card" style={{ marginBottom: 12, cursor: 'pointer' }} onClick={() => navigate('/servicios/' + s.id)}>
            <div style={fila}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{s.nombre}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                  {s.tipo === 'abono' ? 'Abono mensual' : `Renovación anual · vence ${fecha(s.proximo_vencimiento)}`}
                  {s.movimientos ? ` · ${s.movimientos} novedades` : ''}
                </div>
                {s.plataformas && (
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 6 }}>
                    {s.plataformas.split(',').map(x => x.trim()).filter(Boolean).map(x => (
                      <span key={x} className="badge" style={{ background: 'var(--accent-glow)', color: 'var(--accent)', fontSize: 11 }}>{x}</span>
                    ))}
                  </div>
                )}
              </div>
              {s.monto ? <div style={{ fontWeight: 700 }}>{money(s.monto)}</div> : null}
            </div>
          </div>
        ))}
      </>)}

      {!d.proyectos.length && !d.servicios.length && <p style={{ color: 'var(--text-muted)' }}>Todavía no tenés proyectos ni servicios cargados.</p>}
    </div>
  );
}

/* Historial compartido por proyectos y servicios */
function Historial({ movimientos }) {
  if (!movimientos.length) return <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Todavía no hay novedades cargadas.</p>;
  return movimientos.map(m => (
    <div key={m.id} style={{ display: 'flex', gap: 12, paddingBottom: 18 }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: 'var(--accent)', marginTop: 5, flexShrink: 0 }} />
        <span style={{ flex: 1, width: 1, background: 'var(--border)' }} />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fecha(m.fecha)}{TIPO_MOV[m.tipo] ? ` · ${TIPO_MOV[m.tipo]}` : ''}</div>
        <div style={{ fontWeight: 600, fontSize: 14, marginTop: 2 }}>{m.titulo}</div>
        {m.detalle && <div style={{ fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'pre-line', marginTop: 4, lineHeight: 1.6 }}>{m.detalle}</div>}
        {m.archivo_url && <a href={externo(m.archivo_url)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>Abrir enlace</a>}
      </div>
    </div>
  ));
}

function DetalleProyecto() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get('/proyectos/' + id).then(r => setD(r.data)).catch(() => {}); }, [id]);
  if (!d) return <div style={{ color: 'var(--text-muted)' }}>Cargando...</div>;
  const p = d.proyecto;
  const e = ESTADO_PROY[p.estado] || {};
  return (
    <div>
      <button className="btn btn-ghost btn-sm" onClick={() => navigate('/proyectos')} style={{ marginBottom: 12 }}>← Volver</button>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800 }}>{p.nombre}</h1>
        <span className="badge" style={{ background: 'transparent', border: '1px solid var(--border)', color: e.color }}>{e.label || p.estado}</span>
      </div>
      {p.resumen && <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 14, lineHeight: 1.6 }}>{p.resumen}</p>}
      {p.drive_url && (
        <a className="btn btn-secondary btn-sm" href={externo(p.drive_url)} target="_blank" rel="noopener noreferrer" style={{ marginBottom: 18 }}>
          <FolderOpen size={15} /> Carpeta de archivos
        </a>
      )}

      <div className="card" style={{ marginTop: 18, marginBottom: 18 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 14 }}>Historial</h3>
        <Historial movimientos={d.movimientos} />
      </div>

      {d.cobros.length > 0 && (
        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Pagos</h3>
          {d.cobros.map(c => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '9px 0', borderTop: '1px solid var(--border)', fontSize: 13, opacity: c.habilitado ? 1 : 0.55 }}>
              <span>{c.concepto}
                <span style={{ color: 'var(--text-muted)', fontSize: 11 }}> · {c.habilitado ? (ESTADOS[c.estado] || {}).label : 'todavía no disponible'}</span>
              </span>
              <b>{money(c.monto)}</b>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const MESES_CORTO = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const periodoCorto = (p) => { const [a, m] = String(p || '').split('-'); return `${MESES_CORTO[Number(m) - 1] || ''} ${String(a).slice(2)}`; };
const periodoLargo = (p) => {
  const largos = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
  const [a, m] = String(p || '').split('-');
  return `${largos[Number(m) - 1] || ''} ${a || ''}`;
};
const sumar = (plats, campo) => (plats || []).reduce((a, x) => a + Number(x[campo] || 0), 0);

/* Reportes de campañas: los números del mes y la evolución */
function Reportes({ reportes }) {
  const [abierto, setAbierto] = useState(reportes[0] ? reportes[0].id : null);
  if (!reportes.length) return null;

  const serie = [...reportes].reverse().map(r => {
    const plats = (r.metricas || {}).plataformas || [];
    const inversion = sumar(plats, 'inversion');
    const resultados = sumar(plats, 'resultados');
    return { mes: periodoCorto(r.periodo), inversion, resultados, costo: resultados ? Math.round(inversion / resultados) : null };
  });
  const ultimo = serie[serie.length - 1] || {};
  const previo = serie[serie.length - 2];
  const variacion = (campo, mejorSiBaja) => {
    if (!previo || !previo[campo] || !ultimo[campo]) return null;
    const pct = Math.round(((ultimo[campo] / previo[campo]) - 1) * 1000) / 10;
    return { pct, bueno: mejorSiBaja ? pct <= 0 : pct >= 0 };
  };
  const actual = reportes.find(r => r.id === abierto) || reportes[0];
  const plats = (actual.metricas || {}).plataformas || [];
  const destacados = (actual.metricas || {}).destacados || [];
  const dato = { fontSize: 12, color: 'var(--text-muted)' };
  const tooltipStyle = { background: 'var(--tooltip-bg)', border: '1px solid var(--border)', borderRadius: 8, fontFamily: 'Raleway', color: 'var(--text-primary)' };
  const tick = { fill: 'var(--chart-text)', fontSize: 11 };
  const Variacion = ({ v }) => !v ? null : (
    <span style={{ fontSize: 12, fontWeight: 700, color: v.bueno ? '#22c55e' : '#ef4444' }}> {v.pct > 0 ? '+' : ''}{v.pct}%</span>
  );

  return (
    <div className="card" style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700 }}>Resultados</h3>
        <select value={abierto || ''} onChange={e => setAbierto(Number(e.target.value))} style={{ width: 'auto', fontSize: 13 }}>
          {reportes.map(r => <option key={r.id} value={r.id}>{periodoLargo(r.periodo)}</option>)}
        </select>
      </div>

      {actual.conclusion && (
        <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.7, whiteSpace: 'pre-line', margin: '10px 0 16px' }}>{actual.conclusion}</div>
      )}

      {destacados.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 18 }}>
          {destacados.map((x, i) => (
            <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px' }}>
              <div style={dato}>{x.label}</div>
              <div style={{ fontSize: 20, fontWeight: 800 }}>{x.valor}
                {x.variacion && <span style={{ fontSize: 12, fontWeight: 700, color: String(x.variacion).startsWith('-') ? '#ef4444' : '#22c55e' }}> {x.variacion}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {plats.length > 0 && (
        <div style={{ overflowX: 'auto', marginBottom: 18 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 420 }}>
            <thead><tr>
              {['Plataforma', 'Inversión', 'Impresiones', 'Clics', 'Resultados'].map(h => (
                <th key={h} style={{ textAlign: h === 'Plataforma' ? 'left' : 'right', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, padding: '6px 8px' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>{plats.map((x, i) => (
              <tr key={i}>
                <td style={{ padding: '9px 8px', borderTop: '1px solid var(--border)', fontSize: 13, fontWeight: 600 }}>{x.nombre}</td>
                <td style={{ padding: '9px 8px', borderTop: '1px solid var(--border)', fontSize: 13, textAlign: 'right' }}>{x.inversion ? money(x.inversion) : '—'}</td>
                <td style={{ padding: '9px 8px', borderTop: '1px solid var(--border)', fontSize: 13, textAlign: 'right' }}>{Number(x.impresiones || x.alcance || 0).toLocaleString('es-AR') || '—'}</td>
                <td style={{ padding: '9px 8px', borderTop: '1px solid var(--border)', fontSize: 13, textAlign: 'right' }}>{Number(x.clics || 0).toLocaleString('es-AR')}</td>
                <td style={{ padding: '9px 8px', borderTop: '1px solid var(--border)', fontSize: 13, textAlign: 'right', fontWeight: 700 }}>{Number(x.resultados || 0).toLocaleString('es-AR')}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 18 }}>
        <div><div style={dato}>Inversión del mes</div><div style={{ fontSize: 19, fontWeight: 800 }}>{money(ultimo.inversion)}<Variacion v={variacion('inversion')} /></div></div>
        <div><div style={dato}>Resultados</div><div style={{ fontSize: 19, fontWeight: 800 }}>{ultimo.resultados || '—'}<Variacion v={variacion('resultados')} /></div></div>
        <div><div style={dato}>Costo por resultado</div><div style={{ fontSize: 19, fontWeight: 800 }}>{ultimo.costo ? money(ultimo.costo) : '—'}<Variacion v={variacion('costo', true)} /></div></div>
      </div>

      {serie.length > 1 && (<>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Inversión y resultados</div>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={serie} margin={{ left: -8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="mes" tick={tick} />
            <YAxis yAxisId="i" tick={tick} tickFormatter={v => '$' + Math.round(v / 1000) + 'k'} width={58} />
            <YAxis yAxisId="r" orientation="right" tick={tick} width={34} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [n === 'Inversión' ? money(v) : v, n]} />
            <Legend wrapperStyle={{ fontSize: 12, color: 'var(--chart-text)' }} />
            <Bar yAxisId="i" dataKey="inversion" name="Inversión" fill="var(--accent)" radius={[6, 6, 0, 0]} />
            <Bar yAxisId="r" dataKey="resultados" name="Resultados" fill="#22c55e" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>

        {serie.some(s => s.costo) && (<>
          <div style={{ fontSize: 13, fontWeight: 700, margin: '16px 0 8px' }}>Costo por resultado</div>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={serie} margin={{ left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="mes" tick={tick} />
              <YAxis tick={tick} tickFormatter={v => '$' + Math.round(v / 1000) + 'k'} width={58} />
              <Tooltip contentStyle={tooltipStyle} formatter={v => money(v)} />
              <Line type="monotone" dataKey="costo" name="Costo por resultado" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </>)}
      </>)}

      {actual.documento_url && (
        <a className="btn btn-secondary btn-sm" href={externo(actual.documento_url)} target="_blank" rel="noopener noreferrer" style={{ marginTop: 16 }}>
          <FileText size={15} /> Ver el informe de {periodoLargo(actual.periodo)}
        </a>
      )}
    </div>
  );
}

function DetalleServicio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get('/servicios/' + id).then(r => setD(r.data)).catch(() => {}); }, [id]);
  if (!d) return <div style={{ color: 'var(--text-muted)' }}>Cargando...</div>;
  const s = d.servicio;
  const dato = { fontSize: 12, color: 'var(--text-muted)' };
  return (
    <div>
      <button className="btn btn-ghost btn-sm" onClick={() => navigate('/proyectos')} style={{ marginBottom: 12 }}>← Volver</button>
      <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>{s.nombre}</h1>
      {s.plataformas && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {s.plataformas.split(',').map(x => x.trim()).filter(Boolean).map(x => (
            <span key={x} className="badge" style={{ background: 'var(--accent-glow)', color: 'var(--accent)' }}>{x}</span>
          ))}
        </div>
      )}

      <div className="card" style={{ marginBottom: 18 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14 }}>
          <div><div style={dato}>Estado</div><div style={{ fontWeight: 700, color: s.estado === 'activo' ? '#22c55e' : 'var(--text-primary)' }}>
            {s.estado === 'activo' ? 'Activo' : s.estado === 'pausado' ? 'Pausado' : 'Dado de baja'}</div></div>
          <div><div style={dato}>Modalidad</div><div style={{ fontWeight: 700 }}>{s.tipo === 'abono' ? 'Abono mensual' : s.tipo === 'anual' ? 'Renovación anual' : 'Pago único'}</div></div>
          {s.proximo_vencimiento && <div><div style={dato}>Próxima renovación</div><div style={{ fontWeight: 700 }}>{fecha(s.proximo_vencimiento)}</div></div>}
          {s.monto ? <div><div style={dato}>Importe</div><div style={{ fontWeight: 700 }}>{money(s.monto)}</div></div> : null}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
          {s.web_url && <a className="btn btn-secondary btn-sm" href={externo(s.web_url)} target="_blank" rel="noopener noreferrer"><Globe size={15} /> Ver el sitio</a>}
          {s.drive_url && <a className="btn btn-secondary btn-sm" href={externo(s.drive_url)} target="_blank" rel="noopener noreferrer"><FolderOpen size={15} /> Archivos y facturas</a>}
        </div>
      </div>

      {(d.reportes || []).length > 0 && <Reportes reportes={d.reportes} />}

      {s.panel_url && (
        <div className="card" style={{ marginBottom: 18 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Tus datos</h3>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>Información actualizada de tus campañas.</div>
          <div style={{ position: 'relative', width: '100%', paddingTop: '75%', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border)' }}>
            <iframe title="Panel de datos" src={s.panel_url} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }} allowFullScreen />
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: 18 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 14 }}>Actividad</h3>
        <Historial movimientos={d.movimientos} />
      </div>

      {d.cobros.length > 0 && (
        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Últimos cobros</h3>
          {d.cobros.map(c => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '9px 0', borderTop: '1px solid var(--border)', fontSize: 13, opacity: c.habilitado ? 1 : 0.55 }}>
              <span>{c.concepto}<span style={{ color: 'var(--text-muted)', fontSize: 11 }}> · {fecha(c.vencimiento)}</span></span>
              <b>{money(c.monto)}</b>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* Campanita: muestra el contador y lleva a la sección de avisos */
function Campanita() {
  const [sinLeer, setSinLeer] = useState(0);
  const navigate = useNavigate();
  useEffect(() => {
    const cargar = () => api.get('/notificaciones').then(r => setSinLeer(r.data.sin_leer)).catch(() => {});
    cargar();
    const t = setInterval(cargar, 120000);
    return () => clearInterval(t);
  }, []);
  return (
    <button onClick={() => navigate('/notificaciones')} title="Notificaciones" aria-label="Notificaciones"
      style={{ position: 'relative', background: 'transparent', border: 'none', cursor: 'pointer', padding: 6, color: 'rgba(255,255,255,0.82)', display: 'inline-flex' }}>
      <Bell size={18} />
      {sinLeer > 0 && (
        <span style={{ position: 'absolute', top: 0, right: 0, minWidth: 16, height: 16, padding: '0 4px', borderRadius: 8,
          background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {sinLeer > 9 ? '9+' : sinLeer}
        </span>
      )}
    </button>
  );
}

/* Avisos de la cuenta */
function Notificaciones() {
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    api.get('/notificaciones').then(r => { setD(r.data); api.put('/notificaciones/leidas').catch(() => {}); }).catch(() => {});
  }, []);
  if (!d) return <div style={{ color: 'var(--text-muted)' }}>Cargando...</div>;
  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 18 }}>Notificaciones</h1>
      {d.notificaciones.length ? d.notificaciones.map(n => (
        <div key={n.id} className="card" style={{ marginBottom: 10, cursor: n.url ? 'pointer' : 'default', borderLeft: n.leida_at ? '1px solid var(--border)' : '3px solid var(--accent)' }}
          onClick={() => n.url && navigate(n.url)}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{n.titulo}</div>
          {n.cuerpo && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 3 }}>{n.cuerpo}</div>}
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{fecha(n.created_at)}</div>
        </div>
      )) : <p style={{ color: 'var(--text-muted)' }}>Todavía no tenés novedades.</p>}
    </div>
  );
}

/* ---------------- Modal de pago ---------------- */
function ModalPago({ cobro, onClose }) {
  const [medios, setMedios] = useState([]);
  const [form, setForm] = useState({ fecha: new Date().toISOString().slice(0, 10), monto: cobro.monto, notas: '' });
  const [archivo, setArchivo] = useState(null);
  const [enviando, setEnviando] = useState(false);
  useEffect(() => { api.get('/medios-pago').then(r => setMedios(r.data)).catch(() => {}); }, []);

  const enviar = async (e) => {
    e.preventDefault();
    setEnviando(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (archivo) fd.append('comprobante', archivo);
      await api.post(`/cobros/${cobro.id}/informar-pago`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('¡Gracias! Registramos tu aviso de pago');
      onClose(true);
    } catch { toast.error('No se pudo registrar'); }
    finally { setEnviando(false); }
  };

  return (
    <div className="modal-overlay" onClick={() => onClose(false)}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <h2>Informar un pago</h2>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '6px 0 14px' }}>{cobro.concepto} · {money(cobro.monto)}</p>
        {medios.length > 0 && (
          <div style={{ background: 'var(--bg-hover)', borderRadius: 10, padding: 12, marginBottom: 14, fontSize: 13 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Datos para transferir</div>
            {medios.map(m => <div key={m.id} style={{ marginBottom: 4 }}><b>{m.nombre}</b>{m.datos ? <div style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-line' }}>{m.datos}</div> : null}</div>)}
          </div>
        )}
        <form onSubmit={enviar}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group"><label>Fecha del pago</label><input type="date" value={form.fecha} onChange={e => setForm({ ...form, fecha: e.target.value })} required /></div>
            <div className="form-group"><label>Monto</label><input type="number" step="0.01" value={form.monto} onChange={e => setForm({ ...form, monto: e.target.value })} required /></div>
          </div>
          <div className="form-group"><label>Comprobante (opcional)</label>
            <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}><Upload size={14} /> {archivo ? archivo.name.slice(0, 26) : 'Subir archivo'}
              <input type="file" accept="image/*,.pdf" onChange={e => setArchivo(e.target.files[0])} style={{ display: 'none' }} /></label>
          </div>
          <div className="form-group"><label>Comentario (opcional)</label><input value={form.notas} onChange={e => setForm({ ...form, notas: e.target.value })} /></div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 10 }}>
            <button type="button" className="btn btn-secondary" onClick={() => onClose(false)}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={enviando}><CheckCircle2 size={16} /> {enviando ? 'Enviando...' : 'Avisar el pago'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ---------------- Cambiar contraseña ---------------- */
function ModalPassword({ onClose }) {
  const [f, setF] = useState({ actual: '', nueva: '', repetir: '' });
  const guardar = async (e) => {
    e.preventDefault();
    if (f.nueva !== f.repetir) return toast.error('Las contraseñas no coinciden');
    try { await api.put('/password', f); toast.success('Contraseña actualizada'); onClose(); }
    catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
        <h2>Cambiar contraseña</h2>
        <form onSubmit={guardar}>
          <div className="form-group"><label>Contraseña actual</label><input type="password" value={f.actual} onChange={e => setF({ ...f, actual: e.target.value })} required /></div>
          <div className="form-group"><label>Nueva (mínimo 8)</label><input type="password" minLength={8} value={f.nueva} onChange={e => setF({ ...f, nueva: e.target.value })} required /></div>
          <div className="form-group"><label>Repetir</label><input type="password" value={f.repetir} onChange={e => setF({ ...f, repetir: e.target.value })} required /></div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 12 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn btn-primary">Guardar</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* Si algo se rompe al pintar, mostramos un aviso en vez de una pantalla vacía */
class Limite extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error) { console.error('portal:', error); }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <PantallaAcceso titulo="Algo no cargó bien" bajada="Probá recargar la página. Si sigue pasando, escribinos y lo revisamos.">
        <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: 12 }} onClick={() => window.location.reload()}>Recargar</button>
        <button className="btn btn-ghost btn-sm" style={{ width: '100%', justifyContent: 'center', marginTop: 10 }}
          onClick={() => { localStorage.removeItem('portal_token'); window.location.href = '/'; }}>Salir de la sesión</button>
      </PantallaAcceso>
    );
  }
}

/* Pie con los datos de contacto, para que el cliente sepa a dónde escribir */
const SOPORTE_MAIL = 'contacto@udream.com.ar';
const SOPORTE_WPP = '541140996263';
function PieSoporte() {
  return (
    <div style={{ marginTop: 40, paddingTop: 20, borderTop: '1px solid var(--border)', fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.7 }}>
      ¿Necesitás una mano con tus proyectos o con el uso de la plataforma? Escribinos:{' '}
      <a href={`mailto:${SOPORTE_MAIL}`}>{SOPORTE_MAIL}</a>
      {' · '}
      <a href={`https://wa.me/${SOPORTE_WPP}`} target="_blank" rel="noopener noreferrer">WhatsApp 11 4099 6263</a>
      <div style={{ fontSize: 11, marginTop: 8 }}>Udream App · udream.com.ar</div>
    </div>
  );
}

/* ---------------- App del portal ---------------- */
export default function PortalApp() {
  return <Limite><PortalInterno /></Limite>;
}

function PortalInterno() {
  const [usuario, setUsuario] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [pagar, setPagar] = useState(null);
  const [cambiarPass, setCambiarPass] = useState(false);
  const [recargar, setRecargar] = useState(0);
  const location = useLocation();
  const [tema, setTema] = useState(leerTema);

  useEffect(() => {
    aplicarTema(leerTema());
    if (!localStorage.getItem('portal_token')) { setCargando(false); return; }
    api.get('/inicio').then(r => setUsuario(r.data.usuario)).catch(() => localStorage.removeItem('portal_token')).finally(() => setCargando(false));
  }, []);

  const salir = () => { localStorage.removeItem('portal_token'); setUsuario(null); };

  if (location.pathname === '/recuperar') return <Recuperar />;
  if (cargando) return <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>Cargando...</div>;
  if (!usuario) return <Login onEntrar={setUsuario} />;

  const link = ({ isActive }) => ({
    display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', borderRadius: 10, fontSize: 14,
    fontWeight: isActive ? 700 : 500, color: isActive ? '#0B1C33' : 'rgba(255,255,255,0.82)',
    background: isActive ? '#fff' : 'rgba(255,255,255,0.10)', textDecoration: 'none',
  });

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-primary)' }}>
      <header style={{ background: DEGRADE, boxShadow: '0 6px 24px rgba(11,28,51,0.18)' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <img src="/icon-192.png?v=2" alt="" style={{ width: 38, height: 38, borderRadius: 11 }} />
          <div style={{ marginRight: 'auto' }}>
            <div style={{ fontWeight: 800, fontSize: 15, lineHeight: 1.15, color: '#fff' }}>{usuario.cliente}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', letterSpacing: 0.4 }}>Portal de clientes · Udream App</div>
          </div>
          <nav style={{ display: 'flex', gap: 4 }}>
            <NavLink to="/" end style={link}><Home size={16} /> Inicio</NavLink>
            {usuario.ve_importes && <NavLink to="/facturas" style={link}><Receipt size={16} /> Facturas</NavLink>}
            <NavLink to="/proyectos" style={link}><FolderKanban size={16} /> Mis servicios</NavLink>
          </nav>
          <Campanita />
          <ThemeToggle theme={tema} onChange={t => { aplicarTema(t); setTema(t); }} color="rgba(255,255,255,0.82)" />
          <button className="btn btn-ghost btn-sm" style={{ color: 'rgba(255,255,255,0.82)' }} onClick={() => setCambiarPass(true)} title="Cambiar contraseña"><Lock size={15} /></button>
          <button className="btn btn-ghost btn-sm" style={{ color: 'rgba(255,255,255,0.82)' }} onClick={salir}><LogOut size={15} /> Salir</button>
        </div>
      </header>

      <main style={{ maxWidth: 1000, margin: '0 auto', padding: '24px 16px 60px' }} key={recargar}>
        <Routes>
          <Route path="/" element={<Inicio onPagar={setPagar} />} />
          <Route path="/facturas" element={<Facturas onPagar={setPagar} />} />
          <Route path="/proyectos" element={<Trabajos />} />
          <Route path="/proyectos/:id" element={<DetalleProyecto />} />
          <Route path="/servicios/:id" element={<DetalleServicio />} />
          <Route path="/notificaciones" element={<Notificaciones />} />
          <Route path="/minutas" element={<Minutas />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        <PieSoporte />
      </main>

      {pagar && <ModalPago cobro={pagar} onClose={(ok) => { setPagar(null); if (ok) setRecargar(r => r + 1); }} />}
      {cambiarPass && <ModalPassword onClose={() => setCambiarPass(false)} />}
    </div>
  );
}
